import React, { useState } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useDeleteData, useUpdateData } from '../../../hooks';
import { convertDate, dateFormat } from '../../../lib/utils';
import { ProderPlan, ReadinessGate, ReadinessStatus } from '../../../types';

const { Text } = Typography;

const STATUS_LABEL: Record<ReadinessStatus, string> = {
  AVAILABLE: 'Available',
  NOT_YET: 'Not yet',
  NOT_NEEDED: 'Not needed',
};

interface Draft {
  status: ReadinessStatus | null;
  target: Dayjs | null;
  preprod: Dayjs | null;
  owner: string;
  note: string;
}

/**
 * Pre-production readiness for a new item (spec steps 2-3): approved sample
 * (then request pre-prod), lab test, template/jigs/tools/molds. The planner
 * records each gate after checking with its owner. Re-orders skip it.
 */
const ReadinessBlock: React.FC<{ api: string; plan: ProderPlan }> = ({
  api,
  plan,
}) => {
  const rd = plan.readiness;
  const save = useUpdateData<Record<string, unknown>>(`${api}/readiness`);
  const remove = useDeleteData(`${api}/readiness`);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  if (!rd.applies) {
    return (
      <Space>
        <Text strong>Readiness</Text>
        <Text type={'secondary'}>
          not needed — re-order
          {rd.reorderEvidence ? ` (${rd.reorderEvidence})` : ''}
        </Text>
      </Space>
    );
  }

  const draftOf = (g: ReadinessGate): Draft =>
    drafts[g.gate] ?? {
      status: g.status,
      target: g.targetDate ? dayjs(g.targetDate) : null,
      preprod: g.preprodRequestedOn ? dayjs(g.preprodRequestedOn) : null,
      owner: g.ownerDept ?? g.defaultOwner,
      note: g.note ?? '',
    };
  const setDraft = (g: ReadinessGate, patch: Partial<Draft>) =>
    setDrafts((all) => ({ ...all, [g.gate]: { ...draftOf(g), ...patch } }));

  const onSave = (g: ReadinessGate) => {
    const d = draftOf(g);
    if (!d.status) {
      message.warning('Pick the gate status');
      return;
    }
    save.mutate(
      {
        id: g.gate,
        form: {
          status: d.status,
          targetDate: d.target ? d.target.format(dateFormat) : null,
          preprodRequestedOn: d.preprod ? d.preprod.format(dateFormat) : null,
          ownerDept: d.owner.trim() || null,
          note: d.note.trim() || null,
        },
      },
      {
        onSuccess: () => {
          message.success('Readiness saved');
          setDrafts((all) => {
            const next = { ...all };
            delete next[g.gate];
            return next;
          });
        },
        onError: (e) =>
          message.error(e?.response?.data?.message ?? 'Save failed'),
      },
    );
  };

  const columns = [
    {
      title: 'Gate',
      render: (_: unknown, g: ReadinessGate) => (
        <Space direction={'vertical'} size={0}>
          <Text strong>{g.label}</Text>
          {g.status === null ? (
            <Tag className={'text-xs'}>not recorded</Tag>
          ) : (
            <Text type={'secondary'} className={'text-xs'}>
              {STATUS_LABEL[g.status]} · by {g.recordedBy ?? '—'}{' '}
              {g.recordedAt ?? ''}
            </Text>
          )}
          {g.atRisk && (
            <Tag color={'red'} className={'text-xs'}>
              target after the first planned start
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      render: (_: unknown, g: ReadinessGate) => (
        <Select
          size={'small'}
          style={{ width: 130 }}
          placeholder={'not recorded'}
          value={draftOf(g).status ?? undefined}
          onChange={(v) => setDraft(g, { status: v })}
          options={(Object.keys(STATUS_LABEL) as ReadinessStatus[]).map(
            (k) => ({ value: k, label: STATUS_LABEL[k] }),
          )}
        />
      ),
    },
    {
      title: 'Target / pre-prod',
      render: (_: unknown, g: ReadinessGate) => {
        const d = draftOf(g);
        return (
          <Space direction={'vertical'} size={2}>
            {d.status === 'NOT_YET' && (
              <DatePicker
                size={'small'}
                placeholder={'ready by (owner)'}
                value={d.target}
                onChange={(v) => setDraft(g, { target: v })}
              />
            )}
            {g.gate === 'SAMPLE' && (
              <DatePicker
                size={'small'}
                placeholder={'pre-prod requested on'}
                value={d.preprod}
                onChange={(v) => setDraft(g, { preprod: v })}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Owner / note',
      render: (_: unknown, g: ReadinessGate) => {
        const d = draftOf(g);
        return (
          <Space direction={'vertical'} size={2}>
            <Input
              size={'small'}
              style={{ width: 90 }}
              value={d.owner}
              onChange={(e) => setDraft(g, { owner: e.target.value })}
            />
            <Input
              size={'small'}
              placeholder={'note'}
              value={d.note}
              onChange={(e) => setDraft(g, { note: e.target.value })}
            />
          </Space>
        );
      },
    },
    {
      title: '',
      render: (_: unknown, g: ReadinessGate) => (
        <Space direction={'vertical'} size={4}>
          <Button size={'small'} type={'primary'} onClick={() => onSave(g)}>
            Save
          </Button>
          {g.status !== null && (
            <Button
              size={'small'}
              danger
              onClick={() =>
                remove.mutate(
                  { id: g.gate },
                  {
                    onSuccess: () => message.success('Gate cleared'),
                    onError: (e) =>
                      message.error(
                        e?.response?.data?.message ?? 'Remove failed',
                      ),
                  },
                )
              }
            >
              Clear
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className={'space-y-2'}>
      <Space>
        <Text strong>Readiness</Text>
        <Text type={'secondary'}>
          {rd.reorder === null
            ? 'new or re-order unknown (no SKU on its sets)'
            : 'new item'}{' '}
          · {rd.done} of {rd.total} gates ready
        </Text>
        {plan.processes.some((p) => p.saved) && (
          <Text type={'secondary'} className={'text-xs'}>
            first planned start{' '}
            {convertDate(
              plan.processes
                .map((p) => p.saved?.plannedStart)
                .filter((d): d is string => !!d)
                .sort()[0],
              dateFormat,
            )}
          </Text>
        )}
      </Space>
      <Table
        size={'small'}
        rowKey={'gate'}
        pagination={false}
        dataSource={rd.gates}
        columns={columns}
      />
    </div>
  );
};

export default ReadinessBlock;
